import React from 'react';
import { Typography, Button, Box, Paper, Grid, Container, CircularProgress } from '@mui/material';
import { styled, keyframes } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectGameId,
} from '../gamestate/gameStateSlice';
import {
  selectPlayer,
  selectAllPlayers,
  selectPlayersReadinessForInstructions,
  updatePlayerStateAction,
} from '../player/playerSlice';
import { PLAYER_STATES, PLAYER_PHASES } from '../player/playerConstants';
import Background from 'features/components/BackgroundEffect';

// Define brand colors
const ACCENT_TEAL = '#2ec4b6';
const ACCENT_YELLOW = '#ffe066';
const TEXT_COLOR = '#222';
const CARD_BG = '#fff';

// Animation keyframes
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(46, 196, 182, 0.7);
  }
  70% {
    box-shadow: 0 0 0 10px rgba(46, 196, 182, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(46, 196, 182, 0);
  }
`;

const Root = styled('div')({
  width: '100%',
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px',
  overflow: 'auto',
  position: 'relative',
});

const InstructionsCard = styled(Paper)(({ theme }) => ({
  backgroundColor: CARD_BG,
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
  padding: theme.spacing(3, 2),
  maxWidth: 450,
  width: '100%',
  animation: `${fadeInUp} 0.6s ease-out`,
  marginBottom: 80, // Space for the button at bottom
}));

const Title = styled(Typography)({
  fontFamily: "'Gyparody', 'Bangers', 'Impact', sans-serif",
  fontSize: '1.75rem',
  fontWeight: 800,
  color: TEXT_COLOR,
  marginBottom: 16,
  textAlign: 'center',
  textShadow: '1px 1px 0px rgba(46, 196, 182, 0.3)',
});

const InstructionItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: theme.spacing(2),
  animation: `${fadeInUp} 0.4s ease-out forwards`,
  opacity: 0,
}));

const Emoji = styled(Typography)({
  fontSize: '1.4rem',
  marginRight: '12px',
  lineHeight: 1.2,
});

const InstructionText = styled(Typography)({
  fontSize: '1rem',
  fontWeight: 500,
  color: TEXT_COLOR,
  lineHeight: 1.3,
  flexGrow: 1,
});

const ReadyButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(120deg, ${ACCENT_YELLOW} 0%, ${ACCENT_TEAL} 50%, ${ACCENT_YELLOW} 100%)`,
  backgroundSize: '200% 200%',
  backgroundPosition: '0% 40%',
  color: TEXT_COLOR,
  padding: theme.spacing(1.5, 4),
  borderRadius: 12,
  fontWeight: 700,
  fontSize: '1.2em',
  textTransform: 'none',
  boxShadow: '0 4px 16px rgba(44,62,80,0.40) !important',
  minWidth: 240,
  position: 'fixed',
  bottom: theme.spacing(4),
  left: '50%',
  transform: 'translateX(-50%)',
  transition: 'background-position 0.5s cubic-bezier(0.4,0,0.2,1), box-shadow 0s',
  animation: `${pulse} 2s infinite`,
  '&:hover': {
    backgroundPosition: '100% 60%',
    boxShadow: '0 4px 16px rgba(44,62,80,0.40) !important',
  },
  '&.Mui-disabled': {
    background: '#e8e0c0',
    color: '#777570',
    boxShadow: 'none !important',
    cursor: 'not-allowed',
  }
}));

const WaitingCard = styled(Paper)(({ theme }) => ({
  backgroundColor: CARD_BG,
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
  padding: theme.spacing(4, 3),
  maxWidth: 450,
  width: '100%',
  animation: `${fadeInUp} 0.6s ease-out`,
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const WaitingText = styled(Typography)({
  fontSize: '1.2rem',
  fontWeight: 600,
  color: TEXT_COLOR,
  marginBottom: 8,
});

const WaitingDescription = styled(Typography)({
  fontSize: '1rem',
  color: TEXT_COLOR,
  marginBottom: 16,
});

const ReadyPlayerCount = styled(Typography)({
  fontSize: '0.9rem',
  color: '#666',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
});

// Instructions data - same as the host view but optimized for mobile
const instructionsData = [
  { emoji: '🎤', text: "Here's how Groupardy works…" },
  { emoji: '🧠', text: "Everyone answers at the same time. Fast thinkers win." },
  { emoji: '📱', text: "Use your phone to answer before time runs out." },
  { emoji: '✅', text: "Right answers earn points. Wrong answers lose points." },
  { emoji: '⚡', text: "Fastest correct answer? +10 bonus points and control the board!" },
  { emoji: '🎯', text: "Pick your next clue from your phone." },
  { emoji: '🃏', text: "Daily Doubles - wager big, win (or lose) big." },
  { emoji: '🏁', text: "Final round has one question with all-or-nothing wager." },
  { emoji: '😎', text: "We'll do a practice round first (scores reset after)." },
  { emoji: '👉', text: "Click \"I'm ready\" when you're ready to play!" }
];

const PlayerInstructionsScreen = () => {
  const dispatch = useDispatch();
  const gameId = useSelector(selectGameId);
  const player = useSelector(selectPlayer);
  const allPlayers = useSelector(selectAllPlayers);
  const playersReadiness = useSelector(selectPlayersReadinessForInstructions);
  
  // Check if player is ready
  const isPlayerReady = player?.playerState?.[PLAYER_PHASES.INSTRUCTIONS]?.state === PLAYER_STATES.INSTRUCTIONS.READY;
  
  // Count ready players
  const readyPlayerCount = Object.values(playersReadiness).filter(p => p.isReady).length;
  const totalPlayerCount = allPlayers.length;

  const handleReady = () => {
    if (gameId && player && !isPlayerReady) {
      dispatch(updatePlayerStateAction(
        gameId,
        player.id,
        PLAYER_PHASES.INSTRUCTIONS,
        { state: PLAYER_STATES.INSTRUCTIONS.READY }
      ));
    }
  };
  
  // If player isn't ready yet, show instructions
  if (!isPlayerReady) {
    return (
      <Root>
        <Background />
        <InstructionsCard>
          <Title>Game Instructions</Title>
          
          <Container disableGutters>
            <Grid container spacing={1}>
              {instructionsData.map((item, index) => (
                <Grid item xs={12} key={index}>
                  <InstructionItem style={{ animationDelay: `${index * 0.05}s` }}>
                    <Emoji>{item.emoji}</Emoji>
                    <InstructionText>{item.text}</InstructionText>
                  </InstructionItem>
                </Grid>
              ))}
            </Grid>
          </Container>
        </InstructionsCard>
        
        <ReadyButton 
          onClick={handleReady}
          endIcon={<span style={{ fontSize: '1.2rem' }}>✓</span>}
        >
          I'm Ready!
        </ReadyButton>
      </Root>
    );
  }
  
  // If player is ready, show waiting screen
  return (
    <Root>
      <Background />
      <WaitingCard>
        <Typography 
          variant="h5" 
          component="h1" 
          fontFamily="'Gyparody', 'Bangers', 'Impact', sans-serif"
          sx={{ mb: 2, color: ACCENT_TEAL }}
        >
          You're Ready!
        </Typography>
        
        <Box sx={{ fontSize: '3rem', mb: 2 }}>
          👍
        </Box>
        
        <WaitingText>
          Waiting for other players...
        </WaitingText>
        
        <WaitingDescription>
          The game will start automatically when everyone is ready.
        </WaitingDescription>
        
        <ReadyPlayerCount>
          <CircularProgress 
            size={16} 
            thickness={6}
            sx={{ color: ACCENT_TEAL }} 
          />
          {readyPlayerCount} of {totalPlayerCount} players ready
        </ReadyPlayerCount>
      </WaitingCard>
    </Root>
  );
};

export default PlayerInstructionsScreen; 